/**
 * WebSocket client per .product/design.md §4.
 *
 * Protocol:
 *  - First frame after connect: `{type:"auth", token}` (NOT query token — design §4.1).
 *  - Reconnect: send `{type:"resume", last_event_id}`.
 *  - Heartbeat: `{type:"ping"}` every 30s.
 *  - 24h stream TTL — `resume.stale` triggers full refresh.
 */

import { env } from '@/utils/env';
import { logger } from '@/utils/logger';
import { tokenStore } from '@/api/client';

const STORAGE_LAST_EVENT_ID = 'ola.ws.last_event_id';
const HEARTBEAT_INTERVAL_MS = 30_000;
const INITIAL_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;

export type WsEventType =
  | 'auth.ok'
  | 'auth.error'
  | 'resume.ok'
  | 'resume.stale'
  | 'presence.update'
  | 'chat.message'
  | 'chat.read'
  | 'chat.typing'
  | 'chat.created'
  | 'order.status_changed'
  | 'order.proposed'
  | 'notification'
  | 'saved_search.match'
  | 'pong';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// `any` justified: WS event payloads are unknown shape per event; consumers narrow.
export interface WsEvent<P = unknown> {
  type: WsEventType;
  payload: P;
  event_id?: string;
}

export type WsListener = (event: WsEvent) => void;
export type WsStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'failed';
export type WsStatusListener = (status: WsStatus) => void;

interface WsClientOptions {
  url?: string;
  getToken?: () => string | null;
}

class WsClient {
  private socket: WebSocket | null = null;
  private status: WsStatus = 'idle';
  private listeners = new Set<WsListener>();
  private statusListeners = new Set<WsStatusListener>();
  private backoff = INITIAL_BACKOFF_MS;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private intentionallyClosed = false;
  private readonly url: string;
  private readonly getToken: () => string | null;

  /** In-memory token override for the web SPA cookie flow.
   *
   * On Mini App the access JWT lives in localStorage via `tokenStore`. On web
   * the JWT lives in an HttpOnly cookie that JS can't read; we surface the
   * token in the `/auth/me/` JSON body and stash it here for first-frame WS
   * auth only. Memory-only on purpose — never persisted.
   */
  private memoryToken: string | null = null;

  constructor(options: WsClientOptions = {}) {
    this.url = options.url ?? env.wsUrl;
    this.getToken =
      options.getToken ?? (() => this.memoryToken ?? tokenStore.getAccess());
  }

  /** Set the in-memory WS token (web SPA cookie flow only). */
  setMemoryToken(token: string | null): void {
    this.memoryToken = token;
  }

  connect(): void {
    if (this.status === 'connecting' || this.status === 'open') return;
    this.intentionallyClosed = false;
    this.setStatus('connecting');

    let socket: WebSocket;
    try {
      socket = new WebSocket(this.url);
    } catch (err) {
      logger.error('WS construction failed', err);
      this.setStatus('failed');
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.addEventListener('open', () => this.handleOpen());
    socket.addEventListener('message', (e) => this.handleMessage(e));
    socket.addEventListener('close', () => this.handleClose());
    socket.addEventListener('error', () => {
      logger.warn('WS error');
    });
  }

  disconnect(): void {
    this.intentionallyClosed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopHeartbeat();
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) {
      this.socket.close(1000, 'client_disconnect');
    }
    this.socket = null;
    this.setStatus('closed');
  }

  send(event: { type: string; payload?: unknown }): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      logger.warn('WS send while not open; dropped', event);
      return;
    }
    this.socket.send(JSON.stringify(event));
  }

  on(listener: WsListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStatus(listener: WsStatusListener): () => void {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => this.statusListeners.delete(listener);
  }

  getStatus(): WsStatus {
    return this.status;
  }

  private setStatus(s: WsStatus): void {
    this.status = s;
    for (const l of this.statusListeners) l(s);
  }

  private handleOpen(): void {
    const token = this.getToken();
    if (!token) {
      logger.warn('WS open without token, closing');
      this.socket?.close(4001, 'no_token');
      return;
    }
    this.send({ type: 'auth', payload: { token } });
  }

  private handleMessage(e: MessageEvent): void {
    let data: WsEvent;
    try {
      data = JSON.parse(typeof e.data === 'string' ? e.data : '') as WsEvent;
    } catch (err) {
      logger.warn('WS bad frame', err);
      return;
    }
    if (data.event_id) {
      try {
        localStorage.setItem(STORAGE_LAST_EVENT_ID, data.event_id);
      } catch {
        // ignore
      }
    }
    switch (data.type) {
      case 'auth.ok':
        this.backoff = INITIAL_BACKOFF_MS;
        this.setStatus('open');
        this.startHeartbeat();
        this.maybeResume();
        break;
      case 'auth.error':
        logger.warn('WS auth failed', data.payload);
        this.intentionallyClosed = true;
        this.socket?.close(4002, 'auth_failed');
        this.setStatus('failed');
        break;
      case 'resume.stale':
        try {
          localStorage.removeItem(STORAGE_LAST_EVENT_ID);
        } catch {
          // ignore
        }
        break;
      case 'pong':
        // heartbeat ack, nothing else
        break;
    }
    for (const l of this.listeners) l(data);
  }

  private handleClose(): void {
    this.stopHeartbeat();
    this.socket = null;
    if (this.intentionallyClosed) {
      this.setStatus('closed');
      return;
    }
    this.setStatus('closed');
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) return;
    const delay = this.backoff + Math.random() * 500;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
    this.backoff = Math.min(MAX_BACKOFF_MS, this.backoff * 2);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this.send({ type: 'ping', payload: { ts: Date.now() } });
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private maybeResume(): void {
    let lastId: string | null = null;
    try {
      lastId = localStorage.getItem(STORAGE_LAST_EVENT_ID);
    } catch {
      // ignore
    }
    if (lastId) {
      this.send({ type: 'resume', payload: { last_event_id: lastId } });
    }
  }
}

export const ws = new WsClient();
